import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';

export default function Perfis() {
  const { temPermissao } = useAuth();
  const podeGerenciar = temPermissao('perfis_gerenciar');
  const [perfis, setPerfis] = useState([]);
  const [permissoes, setPermissoes] = useState([]);
  const [editando, setEditando] = useState(null); // { id?, nome, descricao, permissao_ids:[] }
  const [carregando, setCarregando] = useState(true);

  const carregar = async () => {
    setCarregando(true);
    try {
      const [p, perms] = await Promise.all([api.get('/perfis'), api.get('/perfis/permissoes')]);
      setPerfis(p.data);
      setPermissoes(perms.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const abrirNovo = () => setEditando({ nome: '', descricao: '', permissao_ids: [] });

  const abrirEditar = async (id) => {
    try {
      const { data } = await api.get(`/perfis/${id}`);
      setEditando({ id: data.id, nome: data.nome, descricao: data.descricao || '', permissao_ids: data.permissao_ids });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  const togglePerm = (id) => {
    setEditando((e) => ({
      ...e,
      permissao_ids: e.permissao_ids.includes(id)
        ? e.permissao_ids.filter((x) => x !== id)
        : [...e.permissao_ids, id],
    }));
  };

  const salvar = async () => {
    if (!editando.nome.trim()) {
      return Swal.fire({ icon: 'warning', title: 'Nome é obrigatório', confirmButtonColor: '#2e9846' });
    }
    try {
      if (editando.id) {
        await api.put(`/perfis/${editando.id}`, editando);
      } else {
        await api.post('/perfis', editando);
      }
      setEditando(null);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  const excluir = async (p) => {
    if (p.sistema) {
      return Swal.fire({ icon: 'info', title: 'Perfil de sistema', text: 'Não pode ser removido.', confirmButtonColor: '#2e9846' });
    }
    const r = await Swal.fire({
      icon: 'warning',
      title: `Excluir "${p.nome}"?`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Excluir',
    });
    if (!r.isConfirmed) return;
    try {
      await api.delete(`/perfis/${p.id}`);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  // Agrupa permissões por grupo p/ renderizar em colunas.
  const permsPorGrupo = permissoes.reduce((acc, p) => {
    (acc[p.grupo] = acc[p.grupo] || []).push(p);
    return acc;
  }, {});

  return (
    <>
      <div className="page-header-cli">
        <h1 className="page-title-cli"><i className="bi bi-shield-lock"></i> Perfis</h1>
        {podeGerenciar && (
          <button className="btn-verde-cli" onClick={abrirNovo}>
            <i className="bi bi-plus-lg"></i> Novo perfil
          </button>
        )}
      </div>

      {carregando ? (
        <div className="card-cli">Carregando…</div>
      ) : perfis.length === 0 ? (
        <EmptyState icon="bi-shield" titulo="Nenhum perfil cadastrado" />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tabela-cli">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Permissões</th>
                <th>Usuários</th>
                <th style={{ width: 200 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {perfis.map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.nome}</strong>{' '}
                    {p.sistema ? <span className="tag-cli laranja">Sistema</span> : null}
                  </td>
                  <td style={{ color: 'var(--texto-suave)' }}>{p.descricao || '—'}</td>
                  <td>{p.qtd_permissoes}</td>
                  <td>{p.qtd_usuarios}</td>
                  <td>
                    <div className="acoes-row-cli">
                      <button className="btn-ghost-cli" onClick={() => abrirEditar(p.id)}>
                        <i className="bi bi-pencil"></i> {podeGerenciar ? 'Editar' : 'Ver'}
                      </button>
                      {podeGerenciar && !p.sistema && (
                        <button className="btn-ghost-cli" onClick={() => excluir(p)}>
                          <i className="bi bi-trash"></i> Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editando && (
        <div className="sidebar-backdrop-cli" onClick={() => setEditando(null)}>
          <div
            className="card-cli"
            style={{ maxWidth: 640, margin: '40px auto', position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: 'var(--verde-escuro)' }}>
              {editando.id ? 'Editar perfil' : 'Novo perfil'}
            </h2>

            <div style={{ marginBottom: 12 }}>
              <label className="label-cli">Nome</label>
              <input
                className="input-cli"
                value={editando.nome}
                onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
                disabled={!podeGerenciar}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label className="label-cli">Descrição</label>
              <input
                className="input-cli"
                value={editando.descricao}
                onChange={(e) => setEditando({ ...editando, descricao: e.target.value })}
                disabled={!podeGerenciar}
              />
            </div>

            <label className="label-cli">Permissões</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              {Object.entries(permsPorGrupo).map(([grupo, perms]) => (
                <div key={grupo}>
                  <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', color: 'var(--texto-suave)', marginBottom: 6 }}>
                    {grupo}
                  </div>
                  {perms.map((perm) => (
                    <label key={perm.id} style={{ display: 'flex', gap: 8, padding: '4px 0', cursor: podeGerenciar ? 'pointer' : 'default' }}>
                      <input
                        type="checkbox"
                        checked={editando.permissao_ids.includes(perm.id)}
                        onChange={() => podeGerenciar && togglePerm(perm.id)}
                        disabled={!podeGerenciar}
                      />
                      <span title={perm.descricao || ''} style={{ fontSize: '0.9rem' }}>{perm.nome}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn-ghost-cli" onClick={() => setEditando(null)}>
                {podeGerenciar ? 'Cancelar' : 'Fechar'}
              </button>
              {podeGerenciar && (
                <button className="btn-verde-cli" onClick={salvar}>
                  <i className="bi bi-check-lg"></i> Salvar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
